import axios from "axios";
import { getToken, logout } from "../utils/auth";
import { getSessionRealm, restoreSession } from "./keycloak";

/**
 * Attaches the access token to every call to our API, and sends the user back to sign in when
 * the API says the token is no longer good.
 *
 * <p>Components therefore do not need to pass headers themselves. Ones that still do keep
 * working: an explicit header wins, because this only fills in a missing one.
 *
 * <p>Only requests to REACT_APP_BASE_URL get the token — never third-party URLs.
 */
const API_BASE = process.env.REACT_APP_BASE_URL || "";

let installed = false;

export const installApiInterceptors = () => {
  if (installed) {
    return;
  }
  installed = true;

  axios.interceptors.request.use(async (config) => {
    const url = config.url || "";
    if (!API_BASE || !url.startsWith(API_BASE)) {
      return config;
    }
    // A Keycloak session renews in the background; make sure the mirrored token is
    // current before using it, so a long-open tab does not send an expired one.
    if (getSessionRealm()) {
      try {
        await restoreSession();
      } catch (error) {
        console.warn("could not refresh the session before a request:", error);
      }
    }
    const token = getToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      // 401 means the token is missing, expired or unusable: the session is over.
      // 403 is a role problem, so leave the user signed in and let the page report it.
      if (status === 401 && API_BASE && url.startsWith(API_BASE) && getToken()) {
        logout();
      }
      return Promise.reject(error);
    }
  );
};
